/**
 * UI Store
 */

import { defineStore } from 'pinia'
import { ref } from 'vue'

export type Theme = 'dark' | 'light'

export const useUiStore = defineStore('ui', () => {
    // State
    const theme = ref<Theme>('dark')
    const sidebarOpen = ref(true)
    const sidebarCollapsed = ref(false)
    const mobileMenuOpen = ref(false)
    const isLoading = ref(false)
    const loadingMessage = ref('')

    // Visibility tracking for idle recovery
    const isTabVisible = ref(true)

    /**
     * Bumped when tab returns from idle to signal all stores to re-fetch.
     * Individual stores compare their _lastFetchedAt against this value.
     */
    const dataInvalidatedAt = ref(0)

    // Initialize theme from localStorage
    function initializeTheme() {
        const savedTheme = localStorage.getItem('mondai-theme') as Theme | null
        if (savedTheme && ['dark', 'light'].includes(savedTheme)) {
            theme.value = savedTheme
        } else {
            // Check system preference
            const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches
            theme.value = prefersDark ? 'dark' : 'light'
        }
        applyTheme()
    }

    function applyTheme() {
        document.documentElement.setAttribute('data-theme', theme.value)
        localStorage.setItem('mondai-theme', theme.value)
    }

    function toggleTheme() {
        theme.value = theme.value === 'dark' ? 'light' : 'dark'
        applyTheme()
    }

    function setTheme(newTheme: Theme) {
        theme.value = newTheme
        applyTheme()
    }

    // Sidebar
    function toggleSidebar() {
        sidebarOpen.value = !sidebarOpen.value
    }

    function toggleSidebarCollapse() {
        sidebarCollapsed.value = !sidebarCollapsed.value
    }

    function closeMobileMenu() {
        mobileMenuOpen.value = false
    }

    function toggleMobileMenu() {
        mobileMenuOpen.value = !mobileMenuOpen.value
    }

    // Loading state
    function setLoading(loading: boolean, message = '') {
        isLoading.value = loading
        loadingMessage.value = message
    }

    // Visibility / idle recovery
    function setTabVisible(visible: boolean) {
        isTabVisible.value = visible
    }

    /** Mark all store data as stale so next view navigation triggers fresh fetches. */
    function invalidateAllData() {
        dataInvalidatedAt.value = Date.now()
    }


    // Watch for external theme changes (e.g., system preference)
    if (typeof window !== 'undefined') {
        window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (e) => {
            if (!localStorage.getItem('mondai-theme')) {
                theme.value = e.matches ? 'dark' : 'light'
                applyTheme()
            }
        })
    }

    return {
        // State
        theme,
        sidebarOpen,
        sidebarCollapsed,
        mobileMenuOpen,
        isLoading,
        loadingMessage,
        isTabVisible,
        dataInvalidatedAt,

        // Actions
        initializeTheme,
        toggleTheme,
        setTheme,
        toggleSidebar,
        toggleSidebarCollapse,
        closeMobileMenu,
        toggleMobileMenu,
        setLoading,
        setTabVisible,
        invalidateAllData,
    }
})
